<template>
  <div class="step-content" :class="{'close-by': useCaptureUrl}">
    <div class="content__match-type">
      <p class="label"><span>{{ $t('page.registration.step1.labelMatchType') }}</span></p>
      <ui-selectbox :value="matchType"
                    :items="matchTypeOptions"
                    :disabled="isEditMode"
                    width="100%"
                    class="match-option--wrap"
                    @input="$emit('update:matchType', $event), isUrlValidatorEnabled = false, isMatchStringValidatorEnabled = false, isCaptureUrlValidatorEnabled = false" />
    </div>

    <div class="content__url" v-if="!requireDomain">
      <div class="input--wrap">
        <p class="label"><label for="url">{{ $t(matchTypeData.labelKey) }}</label></p>
        <input required
               id="url"
               type="text"
               :value="url"
               :class="{error: urlError && url.length}"
               :placeholder="matchTypeData.urlPlaceholder"
               :disabled="isEditMode"
               @focus="showUrlGuide = true, onSetPageAddFocusContent(2, 'matchType')"
               @blur="showUrlGuide = false, isUrlValidatorEnabled = true"
               @input="$emit('update:url', $event.target.value.trim()), isUrlValidatorEnabled = false">
        <i class="icon icon--check-green-size16"></i>
        <i v-if="!isEditMode" class="icon icon--close14" @click="$emit('update:url', '')"></i>
        <ui-tooltip v-show="showUrlGuide" class="url-guide-tooltip" arrowCenter noApplyCenterAnimate>
          <span v-html="$t('page.registration.step1.url.guide1')"/>
          <br><br>
          <span v-html="$t('page.registration.step1.url.guide2')"/>
        </ui-tooltip>
        <span v-if="urlError && url.length" class="validation-text">
          {{ $t(`common.validationErrorText.${urlError}`) }}
        </span>
      </div>
      <ui-button class="btn-match-result"
                 :disabled="isMatchPreviewDisabled ? true : false"
                 @click.stop="$emit('update:showMatchTypeGuide', true)">
        {{ $t('common.matchConfirm') }}
      </ui-button>
    </div>

    <div v-else class="content__domain content__criterion">
      <div class="input--wrap domain">
        <p class="label"><label for="domain">{{ $t(matchTypeData.labelKey) }}</label></p>
        <input required
               id="domain"
               type="text"
               :value="url"
               :class="{error: urlError && url.length}"
               :placeholder="matchTypeData.urlPlaceholder"
               :disabled="isEditMode"
               @focus="showUrlGuide = true, onSetPageAddFocusContent(2, 'matchType')"
               @blur="showUrlGuide = false"
               @input="$emit('update:url', $event.target.value.trim())">
        <ui-tooltip v-show="showUrlGuide" class="url-guide-tooltip" arrowCenter noApplyCenterAnimate>
          <span v-html="$t('page.registration.step1.url.guide2')"/>
        </ui-tooltip>
        <span v-if="urlError && isUrlValidatorEnabled && url.length" class="validation-text">
          {{ $t(`common.validationErrorText.${urlError}`) }}
        </span>
      </div>

      <div class="input--wrap content__matchType">
        <p class="label"><label for="criterion">{{ $t(matchTypeData.matchStringLabelKey) }}</label></p>
        <input required id="criterion" type="text"
               :class="{error: matchStringError && matchString.length}"
               :value="matchString"
               :placeholder="matchTypeData.matchStringPlaceholder"
               :disabled="isEditMode"
               @focus="onSetPageAddFocusContent(2, 'matchType')"
               @blur="isMatchStringValidatorEnabled = true"
               @input="$emit('update:matchString', $event.target.value.trim()), isMatchStringValidatorEnabled = false">
        <span v-if="matchStringError && isMatchStringValidatorEnabled && matchString.length" class="validation-text">
          {{ $t(`common.validationErrorText.${matchStringError}`) }}
        </span>
      </div>

      <button v-if="matchType !== 'regexp'"
              class="btn-match-result"
              :disabled="isMatchPreviewDisabled"
              @click.stop="$emit('update:showMatchTypeGuide', true)">{{ $t('common.matchConfirm') }}</button>
    </div>

    <match-type-guide
      v-if="showMatchTypeGuide"
      :requireDomain="requireDomain"
      :enteredMatchType="matchType"
      :enteredURL="url"
      :enteredCriterion="matchString"
      :enteredSiteCode="urlSiteCode"
      @closeLayer="$emit('update:showMatchTypeGuide', false)"/>

    <ul class="content__advance--wrap">
      <li class="content__advance content__setURL icon--bg-dash">
        <p class="content__desc">{{ $t('page.registration.step1.setURL.label') }}</p>
        <div class="label--wrap">
          <label for="setURLTrue">
            <input type="radio" name="useCaptureUrl" value="true" id="setURLTrue" :checked="useCaptureUrl"
              @change="onUseCaptureUrlChange($event, true)"><i class="icon icon--radio-off"></i>
            <span>{{ $t('common.yes') }}</span>
          </label>
          <label for="setURLFalse">
            <input type="radio" name="useCaptureUrl" value="false" id="setURLFalse" :checked="!useCaptureUrl"
              @change="onUseCaptureUrlChange($event, false)"><i class="icon icon--radio-off"></i>
            <span>{{ $t('common.no') }}</span>
          </label>
        </div>
        <div v-if="useCaptureUrl" :class="{on: useCaptureUrl}" class="content--wrap" >
          <div class="input--wrap">
            <input type="text" placeholder="https://www.beusable.net/signup/?x=1"
                   :class="{error: captureUrlError && captureUrl.length}"
                   :value="captureUrl"
                   @focus="onSetPageAddFocusContent(2, 6)"
                   @blur="isCaptureUrlValidatorEnabled = true"
                   @input="$emit('update:captureUrl', $event.target.value.trim()), isCaptureUrlValidatorEnabled = false">
            <span v-if="captureUrlError && isCaptureUrlValidatorEnabled && captureUrl.length"
                  class="validation-text">
              {{ $t(`common.validationErrorText.${captureUrlError}`) }}
            </span>
          </div>
        </div>
      </li>

      <li class="content__advance content__bLogin icon--bg-dash"
          :disabled="!allowBLogin">
        <p class="content__desc">
          {{ $t('page.registration.step1.bLogin.label') }}
          <ui-icon-tooltip arrowCenter
            :forceShowText="showBLoginTooltip">
            <span v-html="$t('page.registration.step1.bLogin.tooltip')"></span>
          </ui-icon-tooltip>
        </p>
        <div class="label--wrap"
          @mouseenter="showBLoginTooltip = !allowBLogin"
          @mouseleave="showBLoginTooltip = false">
          <label for="bLoginTrue">
            <input type="radio" name="bLogin" value="true" id="bLoginTrue" :checked="bLogin"
              @change="$emit('update:bLogin', true)"><i class="icon icon--radio-off"></i>
            <span>{{ $t('common.yes') }}</span>
          </label>
          <label for="bLoginFalse">
            <input type="radio" name="bLogin" value="false" id="bLoginFalse" :checked="!bLogin"
              @change="$emit('update:bLogin', false)"><i class="icon icon--radio-off"></i>
            <span>{{ $t('common.no') }}</span>
          </label>
        </div>
      </li>

      <li class="content__advance content__isOnlyPhone icon--bg-dash">
        <p class="content__desc">{{ $t('page.registration.step1.device.label') }}</p>
        <div class="label--wrap">
          <label for="isOnlyPhoneTrue">
            <input type="radio" name="bMobile" value="true" id="isOnlyPhoneTrue" :checked="bMobile"
              @change="$emit('update:bMobile', true)"><i class="icon icon--radio-off"></i>
            <span>{{ $t('common.yes') }}</span>
          </label>
          <label for="isOnlyPhoneFalse">
            <input type="radio" name="bMobile" value="false" id="isOnlyPhoneFalse" :checked="!bMobile"
              @change="$emit('update:bMobile', false)"><i class="icon icon--radio-off"></i>
            <span>{{ $t('common.no') }}</span>
          </label>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import UiButton from '@/components/ui/UiButton'
import UiSelectbox from '@/components/ui/UiSelectbox'
import UiTooltip from '@/components/ui/UiTooltip'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import MatchTypeGuide from '@/components/page-registration/MatchTypeGuide'

export default {
  name: 'RegistrationStep1',
  components: {
    UiButton,
    UiSelectbox,
    UiTooltip,
    UiIconTooltip,
    MatchTypeGuide
  },
  props: [
    'isEditMode',
    'matchTypeOptions',
    'matchTypeData',
    'matchType',
    'url',
    'urlSiteCode',
    'captureUrl',
    'matchString',
    'bLogin',
    'bMobile',
    'showMatchTypeGuide',
    'useCaptureUrl',
    'requireDomain',
    'urlError',
    'matchStringError',
    'captureUrlError'
  ],
  data () {
    return {
      showUrlGuide: false,
      isUrlValidatorEnabled: this.url.length,
      isMatchStringValidatorEnabled: this.matchString.length,
      isCaptureUrlValidatorEnabled: this.captureUrl.length,
      showBLoginTooltip: false
    }
  },
  computed: {
    ...mapState([
      'planData'
    ]),
    isMatchPreviewDisabled () {
      return this.matchType === 'regexp' || this.urlError || this.matchStringError
    },
    allowBLogin () {
      return this.planData.feature.bLogin
    }
  },
  watch: {
    matchType () {
      this.onSetPageAddFocusContent(2, 'matchType')
    },
    bLogin () {
      this.onSetPageAddFocusContent(2, 7)
    },
    bMobile () {
      this.onSetPageAddFocusContent(2, 8)
    },
    isUrlValidatorEnabled (value) {
      this.$emit('validateUrl')
    },
    isMatchStringValidatorEnabled (value) {
      this.$emit('validateUrl')
    },
    isCaptureUrlValidatorEnabled (value) {
      this.$emit('validateUrl')
    }
  },
  methods: {
    ...mapMutations([
      'setPageAddGuideContent'
    ]),
    onSetPageAddFocusContent (step, index) {
      if (index === 'matchType') {
        switch (this.matchType) {
          case 'exact':
            index = Number(0)
            break
          case 'simple':
            index = Number(1)
            break
          case 'start':
            index = Number(2)
            break
          case 'end':
            index = Number(3)
            break
          case 'contains':
            index = Number(4)
            break
          case 'regexp':
            index = Number(5)
            break
        }
      }
      const values = { currentStep: step, currentContentIndex: index }
      this.setPageAddGuideContent(values)
    },
    onUseCaptureUrlChange (event, value) {
      this.$emit('update:useCaptureUrl', value)
      this.onSetPageAddFocusContent(2, 6)

      if (!value) {
        this.isCaptureUrlValidatorEnabled = false
        this.$emit('update:captureUrl', '')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.step-content {
  margin-top: 55px;
  padding-left: 70px;
  padding-right: 70px;
  margin-bottom: 57px;

  &.close-by {
    margin-top: 30px;
  }
}

.content__match-type {
  .match-option--wrap >>> .item--wrap{
    padding-left: 12px;
  }
  .refer-text {
    font-size: 12px;
    line-height: 1.5;
    color: #767676;
    li {
      position: relative;
      padding-left: 13px;
      margin-top: 5px;
      &:nth-of-type(1) {
        margin-top: 9px;
      }
      &:before {
        content: '';
        display: inline-block;
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background-color: #767676;
        position: absolute;
        top: 9px;
        left: 6px;
        transform: translateY(-50%);
      }
    }
  }
}

.content__url, .content__domain {
  @include flexXbetween;
  margin-top: 40px;
  .icon--check-green-size16.pass {
    display: inline-block;
    z-index: 1;
    background-color: #fff;
    border-radius: 12px;
  }
  .btn-match-result {
    @include buttonTypeNormal;
    overflow: visible;
    min-width: auto;
    border: 1px solid #bbbbbb;
    background-color: #ffffff;
    font-size: 14px;
    font-weight: bold;
    margin-top: 18px;
    margin-left: 5px;

    &:hover:after {
      border-radius: 20px;
    }
  }
  .url-guide-tooltip {
    display: inline-block;
    bottom: calc(100% - 2px);
    left: 160px;
    /deep/ .text {
      text-align: left;
      strong {
        color: $brandColor;
        font-weight: bold;
      }
    }
  }
  .speech-bubble {
    display: inline-block;
    bottom: calc(100% - 2px);
    left: 160px;
    .text {
      text-align: left;
      /deep/ strong {
        color: $brandColor;
        font-weight: bold;
      }
    }
  }
}

.content__url {
  position: relative;
  display: flex;
  justify-content: space-between;
  .input--wrap {
    display: block;
    width: 100%;
  }
  #url {
    width: 100%;
    &.pass { border-color: #3eaa08; }
    &.error { border-color: $errorColor; }
  }
}

.content__domain {
  #domain {
    width: 100%;
    &.error {
      border-color: $brandColor;
    }
  }

  .content__matchType {
    margin-left: 5px;
    #criterion {
      width: 100%;
      &.error {
        border-color: $brandColor;
      }
    }

    label,
    .validation-text {
      white-space: nowrap;
    }
  }

  &.content__criterion {
    .input--wrap.domain {
      width: 100%;
    }
  }
}

.content__advance--wrap {
  margin-top: 40px;

  .content__advance {
    margin-top: 28px;
    position: relative;

    &.content__setURL {
      .content--wrap {
        &.on {
          margin-bottom: 39px;
        }
        .input--wrap {
          width: 100%;
          margin-top: 21px;
          input[type=text] {
            width: 100%;

            &.error {
              border-color: $errorColor;
            }
          }
        }
      }
    }

    &.content__bLogin {
      .tooltip {
        display: none;
      }
      &[disabled] {
        /deep/ .tooltip {
          display: inline-block;
          height: 13px;
        }
        .label--wrap label {
          pointer-events: none;
          .icon {
            opacity: 0.3;
          }
        }
      }
    }

    .content__desc {
      display: inline-block;
      font-size: 13px;
      font-weight: bold;
      color: #767676;
      position: relative;
      z-index: 1;
      background-color: #fff;
      padding-right: 6px;
    }

    .label--wrap {
      float: right;
      position: relative;
      z-index: 1;
      background-color: #fff;
      padding-left: 3px;
      white-space: nowrap;

      label {
        position: relative;
        cursor: pointer;

        &:nth-last-of-type(1) {
          margin-left: 40px;
        }

        .icon {
          position: absolute;
          top: 50%;
          left: 0;
          transform: translateY(-50%);
        }

        span {
          display: inline-block;
          padding-left: 26px;
        }
      }
    }
  }
}

.button--wrap {
  margin-top: 50px;
}
</style>
